import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';

const CHECK_ITEMS = [
  { key: 'length',  label: '8+ characters' },
  { key: 'upper',   label: 'Uppercase letter' },
  { key: 'lower',   label: 'Lowercase letter' },
  { key: 'number',  label: 'Number' },
  { key: 'special', label: 'Special character' },
  { key: 'match',   label: 'Passwords match' },
];

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({ newPassword: '', confirmPassword: '' });
  const [status, setStatus] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [isTokenValid, setIsTokenValid] = useState(null); // null = checking, true = valid, false = invalid
  const [checks, setChecks] = useState({
    length: false, upper: false, lower: false, number: false, special: false, match: false
  });

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsTokenValid(false);
        setStatus({ type: 'error', text: 'Missing reset token in URL. Please use the link from your email.' });
        return;
      }
      try {
        await api.get(`/auth/validate-reset-token?token=${token}`);
        setIsTokenValid(true);
      } catch (err) {
        setIsTokenValid(false);
        setStatus({
          type: 'error',
          text: err.response?.data?.detail || 'This password reset link is invalid or has expired.'
        });
      }
    };
    validateToken();
  }, [token]);

  useEffect(() => {
    const password = formData.newPassword;
    const confirmPassword = formData.confirmPassword;
    setChecks({
      length: password.length >= 8,
      upper:  /[A-Z]/.test(password),
      lower:  /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[@$!%*?&]/.test(password),
      match:  password === confirmPassword && password.length > 0,
    });
  }, [formData.newPassword, formData.confirmPassword]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setStatus({ type: 'error', text: 'Missing reset token in URL. Please use the link from your email.' });
      return;
    }

    if (!Object.values(checks).every(Boolean)) {
      setStatus({ type: 'warning', text: 'Please fulfill all password requirements.' });
      return;
    }

    try {
      const response = await api.post('/auth/reset-password', {
        token: token,
        newPassword: formData.newPassword,
      });
      setStatus({ type: 'success', text: response.data.message });
    } catch (err) {
      setStatus({ type: 'error', text: err.response?.data?.detail || 'Invalid or expired token' });
    }
  };

  const statusColors = {
    success: { color: 'var(--color-success)', bg: 'var(--color-success-bg)', border: 'var(--color-success-border)' },
    error:   { color: 'var(--color-error)',   bg: 'var(--color-error-bg)',   border: 'var(--color-error-border)'   },
    warning: { color: 'var(--color-warning)', bg: 'var(--color-warning-bg)', border: 'var(--color-warning-border)' },
  };

  const inputStyle = (field) => ({
    ...styles.input,
    ...(focusedField === field ? styles.inputFocused : {}),
  });

  return (
    <div style={styles.page}>
      <div className="auth-blob-decoration" style={styles.blobTopRight} aria-hidden="true" />
      <div className="auth-blob-decoration" style={styles.blobBottomLeft} aria-hidden="true" />

      <main style={{ ...styles.card, maxWidth: status?.type === 'success' ? '680px' : '420px', transition: 'max-width 0.3s ease-in-out' }} className="glass-panel auth-card-container">
        <header style={styles.brandArea}>
          <span style={styles.logoMark} aria-hidden="true">⬡</span>
          <span style={styles.brandName}>RezFlow</span>
        </header>

        {isTokenValid === null ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center', padding: '20px 0' }}>
            <div style={styles.spinner} />
            <p style={{ ...styles.subtext, marginBottom: 0 }}>Checking link validity...</p>
          </div>
        ) : isTokenValid === false ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center', textAlign: 'center' }}>
            <div style={styles.iconWrapError} aria-hidden="true">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--color-error)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <h2 style={styles.headingError}>Link Expired</h2>
            <p style={{ ...styles.subtext, marginBottom: '10px' }}>
              {status.text || 'This password reset link is invalid or has expired.'}
            </p>
            <Link to="/login" style={styles.primaryLinkBtn} className="hover-lift">
              Back to Sign In
            </Link>
          </div>
        ) : status?.type === 'success' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center', textAlign: 'left' }}>
            <div style={styles.iconWrapSuccess} aria-hidden="true">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>

            <h2 style={styles.heading}>Success!</h2>
            <div style={styles.successContainer}>
              <p style={styles.successParagraph}>
                Your password has been successfully reset.
              </p>
              <p style={styles.successParagraph}>
                You can now sign in using your new password.
              </p>
            </div>

            <Link to="/login" style={styles.primaryLinkBtn} className="hover-lift">
              Go to Sign In
            </Link>
          </div>
        ) : (
          <>
            <div style={styles.iconWrap} aria-hidden="true">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--color-charcoal-600)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>

            <h2 style={styles.heading}>Create new password</h2>
            <p style={styles.subtext}>
              Enter and confirm your new password below. Make it strong!
            </p>

            <form onSubmit={handleSubmit} style={styles.form} noValidate>
              <div style={styles.fieldWrap}>
                <label htmlFor="rp-newPassword" style={styles.label}>New Password</label>
                <input
                  id="rp-newPassword"
                  name="newPassword"
                  type="password"
                  placeholder="••••••••"
                  onChange={handleChange}
                  onFocus={() => setFocusedField('newPassword')}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle('newPassword')}
                  autoComplete="new-password"
                  required
                />
              </div>

              {/* Password strength pills */}
              <div style={styles.checksGrid} role="group" aria-label="Password requirements">
                {CHECK_ITEMS.map(({ key, label }) => (
                  <span
                    key={key}
                    style={{
                      ...styles.checkPill,
                      ...(checks[key] ? styles.checkPillActive : styles.checkPillInactive),
                    }}
                  >
                    <span style={styles.checkIcon} aria-hidden="true">
                      {checks[key] ? '✓' : '○'}
                    </span>
                    {label}
                  </span>
                ))}
              </div>

              <div style={styles.fieldWrap}>
                <label htmlFor="rp-confirmPassword" style={styles.label}>Confirm Password</label>
                <input
                  id="rp-confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  onChange={handleChange}
                  onFocus={() => setFocusedField('confirmPassword')}
                  onBlur={() => setFocusedField(null)}
                  style={{
                    ...inputStyle('confirmPassword'),
                    ...(formData.confirmPassword
                      ? checks.match
                        ? { borderColor: 'var(--color-success)', boxShadow: 'var(--glow-success)' }
                        : { borderColor: 'var(--color-error)',   boxShadow: 'var(--glow-error)'   }
                      : {}),
                  }}
                  autoComplete="new-password"
                  required
                />
              </div>

              {status && status.type !== 'success' && (
                <div style={{
                  ...styles.statusMsg,
                  color: statusColors[status.type]?.color,
                  backgroundColor: statusColors[status.type]?.bg,
                  borderColor: statusColors[status.type]?.border,
                }}>
                  {status.text}
                </div>
              )}

              <button type="submit" style={styles.submitBtn} className="hover-lift">
                Reset Password
              </button>
            </form>
          </>
        )}

        {isTokenValid === true && status?.type !== 'success' && (
          <p style={styles.footerLink}>
            <Link to="/login" style={styles.link}>← Back to Sign In</Link>
          </p>
        )}
      </main>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '24px 20px',
    background: 'linear-gradient(145deg, #f0ede7 0%, #e8eee5 55%, #e7ecf0 100%)',
    position: 'relative', overflow: 'hidden',
  },
  blobTopRight: {
    position: 'absolute', top: '-120px', right: '-140px',
    width: '400px', height: '400px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(22,163,74,0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  blobBottomLeft: {
    position: 'absolute', bottom: '-100px', left: '-120px',
    width: '360px', height: '360px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(35,43,34,0.06) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  card: {
    position: 'relative', zIndex: 1, width: '100%', maxWidth: '420px',
    borderRadius: 'var(--radius-2xl)', padding: '40px 40px 36px',
    animation: 'rz-fadeIn 0.4s ease-out both', textAlign: 'center',
  },
  brandArea: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '24px',
  },
  logoMark: { fontSize: '22px', color: 'var(--color-charcoal-800)', lineHeight: 1 },
  brandName: {
    fontFamily: 'var(--font-heading)', fontSize: '22px', fontWeight: 700,
    letterSpacing: '-0.4px', color: 'var(--color-charcoal-900)',
  },
  iconWrap: {
    width: '72px', height: '72px', borderRadius: '20px',
    backgroundColor: 'var(--color-surface-alt)', border: '1px solid var(--color-border)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 20px', boxShadow: 'var(--shadow-sm)',
  },
  iconWrapSuccess: {
    width: '72px', height: '72px', borderRadius: '20px',
    backgroundColor: 'var(--color-success-bg)', border: '1px solid var(--color-success-border)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 20px', boxShadow: 'var(--shadow-sm)',
  },
  iconWrapError: {
    width: '72px', height: '72px', borderRadius: '20px',
    backgroundColor: 'var(--color-error-bg)', border: '1px solid var(--color-error-border)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 20px', boxShadow: 'var(--shadow-sm)',
  },
  successContainer: {
    display: 'flex', flexDirection: 'column', gap: '12px',
    textAlign: 'left', margin: '8px 0 16px 0',
  },
  successParagraph: {
    fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)',
    lineHeight: '1.6', margin: 0,
  },
  tipBox: {
    padding: '12px', borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--color-success-bg)',
    border: '1px solid var(--color-success-border)',
  },
  heading: {
    fontFamily: 'var(--font-heading)', fontSize: 'var(--text-2xl)',
    fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: '10px',
  },
  headingError: {
    fontFamily: 'var(--font-heading)', fontSize: 'var(--text-2xl)',
    fontWeight: 700, color: 'var(--color-error)', marginBottom: '10px',
  },
  subtext: {
    fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)',
    lineHeight: 'var(--line-height-loose)', marginBottom: '28px',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '16px', textAlign: 'left' },
  fieldWrap: { display: 'flex', flexDirection: 'column', gap: '7px' },
  label: { fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' },
  input: {
    padding: '13px 14px', border: '1.5px solid var(--color-border-strong)',
    borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
    color: 'var(--color-text-primary)', backgroundColor: 'var(--color-surface)',
    outline: 'none', width: '100%',
    transition: 'border-color var(--transition-base), box-shadow var(--transition-base)',
  },
  inputFocused: { borderColor: 'var(--color-charcoal-700)', boxShadow: 'var(--glow-accent)' },
  submitBtn: {
    width: '100%', padding: '14px 20px',
    background: 'linear-gradient(135deg, var(--color-charcoal-800) 0%, var(--color-charcoal-700) 100%)',
    color: 'var(--color-text-on-dark)', border: 'none',
    borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-heading)',
    fontWeight: 'var(--font-weight-bold)', fontSize: 'var(--text-base)',
    boxShadow: '0 4px 14px rgba(13,15,13,0.18)',
    transition: 'opacity var(--transition-base), transform var(--transition-base), box-shadow var(--transition-base)',
  },
  primaryLinkBtn: {
    width: '100%', padding: '14px 20px',
    background: 'linear-gradient(135deg, var(--color-charcoal-800) 0%, var(--color-charcoal-700) 100%)',
    color: 'var(--color-text-on-dark)', border: 'none',
    borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-heading)',
    fontWeight: 'var(--font-weight-bold)', fontSize: 'var(--text-base)',
    textDecoration: 'none', display: 'inline-block', textAlign: 'center',
    boxShadow: '0 4px 14px rgba(13,15,13,0.18)',
    transition: 'opacity var(--transition-base), transform var(--transition-base), box-shadow var(--transition-base)',
  },
  statusMsg: {
    padding: '14px 16px', borderRadius: 'var(--radius-md)',
    border: '1px solid', fontSize: 'var(--text-sm)',
    lineHeight: 'var(--line-height-loose)', animation: 'rz-fadeInFlat 0.2s ease-out',
    textAlign: 'left',
  },
  checksGrid: {
    display: 'flex', flexWrap: 'wrap', gap: '8px',
    padding: '12px', borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--color-surface-alt)',
    border: '1px solid var(--color-border-subtle)',
    margin: '4px 0 12px 0',
  },
  checkPill: {
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    padding: '4px 10px', borderRadius: 'var(--radius-full)',
    fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)',
    border: '1px solid',
    transition: 'all var(--transition-base)',
  },
  checkPillActive: {
    color: 'var(--color-emerald-700)',
    backgroundColor: 'var(--color-emerald-050)',
    borderColor: 'var(--color-emerald-100)',
    animation: 'rz-checkPop 0.25s ease-out',
  },
  checkPillInactive: {
    color: 'var(--color-text-muted)',
    backgroundColor: 'var(--color-surface)',
    borderColor: 'var(--color-border)',
  },
  checkIcon: { fontSize: '10px' },
  footerLink: { textAlign: 'center', marginTop: '24px', fontSize: 'var(--text-sm)' },
  link: { color: 'var(--color-text-secondary)', fontWeight: 'var(--font-weight-medium)' },
  spinner: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: '3px solid rgba(79, 70, 229, 0.1)',
    borderTopColor: '#4f46e5',
    animation: 'rz-spin 0.8s linear infinite',
  },
};

export default ResetPassword;